// ════════════════════════════════════════════════════════════
//  Bracket.jsx — THE BRACKETS tab: every tournament of the night as a real tree you can follow
// ════════════════════════════════════════════════════════════
//  caP'n 2026-10-22: "I want a real deal tournament bracket I can follow for all tourneys big or small cup or not … see the progress
//  of the night, who they fight, who they fight if they win".
//  Reads online/bracket.js (pure); draws: a day picker, a strip of the night's tournaments (progress at a glance), the full tree of the
//  one you pick (winners move right, the loser fades, a "waiting" seat says who feeds it), and — tap a fighter — their road: what
//  they did, who's next and when, and who they'd meet if they keep winning. Friday's Apocalypse is best-of-3 series.
//  Self-contained (its own little portrait chip) so it never imports Circuit.jsx.
// ════════════════════════════════════════════════════════════
import { useEffect, useMemo, useRef, useState } from "react";
import { LINES, portraitUrl } from "../data/roster.js";
import { serverNow } from "../online/ie.js";
import { racingDayOf, hourStart, BEINGS, CHALLENGERS, nameOfKey, hoursOfDay } from "../online/circuit.js";
import { nightOf, storyOf, apoOf, daysOf, nodeAt, cupShort } from "../online/bracket.js";
import { openBeing } from "./circuitStore.js";

const nameOf = (k) => (k ? nameOfKey(k) : "—");
const teamOf = (k) => (BEINGS.get(k) && BEINGS.get(k).team) || (CHALLENGERS.get(k) && CHALLENGERS.get(k).t) || [];
const Chip = ({ k }) => <span className="hb-trio bk-trio">{teamOf(k).slice(0, 3).map((id, i) => LINES[id] ? <i key={i} style={{ backgroundImage: `url(${portraitUrl(id, "beb")})` }} /> : null)}</span>;
const clock = (ms) => new Date(ms).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
const hourTxt = (ms) => new Date(ms).toLocaleTimeString([], { hour: "numeric" });
const dayTxt = (day) => { const hs = hoursOfDay(day); const h = hs[hs.length >> 1]; return h != null ? new Date(hourStart(h)).toLocaleDateString([], { weekday: "short" }) : `Day ${day}`; };
const dur = (s) => `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, "0")}`;
/** "8:12pm" (a bell), "about 8:12pm" (a guess), "in the 9pm hour" */
const whenTxt = (w) => (!w ? "" : w.kind === "bell" ? clock(w.ms) : w.kind === "about" ? `about ${clock(w.ms)}` : `in the ${hourTxt(w.ms)} hour`);

const STATE_TXT = { live: "● LIVE", ringside: "at ringside", bye: "bye", empty: "" };

/** One side of a match. */
function Side({ sd, node, hl, onPick, win, lose, M }) {
  if (sd.pending) {
    const fd = nodeAt(M, sd.from.r, sd.from.i);
    const ns = fd ? [fd.a.key, fd.b.key] : [];
    return <div className="bk-s wait"><span className="bk-n">?</span><em>winner of {ns.every(Boolean) ? ns.map(nameOf).join(" / ") : "the bout before"}</em></div>;
  }
  if (!sd.key) return <div className="bk-s none"><span className="bk-n" /><em>{node.state === "bye" ? "bye" : "—"}</em></div>;
  return (
    <div className={"bk-s" + (win ? " win" : "") + (lose ? " lose" : "") + (hl === sd.key ? " me" : "") + (sd.div ? " d-" + sd.div : "")} role="button" tabIndex={0}
      onClick={() => onPick(sd.key)} onKeyDown={(e) => { if (e.key === "Enter") onPick(sd.key); }}>
      <span className="bk-n">{sd.seed || ""}</span><Chip k={sd.key} /><b>{nameOf(sd.key)}</b>{win && <i className="bk-ok">✓</i>}
    </div>
  );
}
function Match({ node, M, hl, path, onPick }) {
  const w = node.winner, bye = node.state === "bye";
  const foot = node.state === "done" ? (node.row && node.row.t ? dur(node.row.t) : "done") : STATE_TXT[node.state] || whenTxt(node.when);
  return (
    <div className={"bk-m st-" + node.state + (path.has(node.id) ? " path" : "")} data-id={node.id}>
      <Side sd={node.a} node={node} hl={hl} onPick={onPick} win={!!w && w === node.a.key && !bye} lose={!!w && node.loser === node.a.key} M={M} />
      <Side sd={node.b} node={node} hl={hl} onPick={onPick} win={!!w && w === node.b.key && !bye} lose={!!w && node.loser === node.b.key} M={M} />
      {foot && <small className="bk-f">{foot}</small>}
    </div>
  );
}
/** The tree, recursive: a node = [its two feeder bouts] → [itself]. */
function Node({ M, r, i, hl, path, onPick }) {
  const n = nodeAt(M, r, i);
  if (!n) return null;
  return (
    <div className={"bk-node" + (r === 0 ? " leaf" : "")}>
      {r > 0 && <div className="bk-kids">{[2 * i, 2 * i + 1].map((j) => <div key={j} className="bk-kid"><Node M={M} r={r - 1} i={j} hl={hl} path={path} onPick={onPick} /></div>)}</div>}
      <Match node={n} M={M} hl={hl} path={path} onPick={onPick} />
    </div>
  );
}

/** A fighter's road, in words. */
function Story({ M, k, onClose }) {
  const S = storyOf(M, k);
  if (!S) return null;
  const opp = (it) => it.opp ? <b>{nameOf(it.opp)}</b> : it.oppEmpty ? <em>nobody (a bye)</em> : it.oppFrom ? <em>the winner of {it.oppFrom.names.every(Boolean) ? it.oppFrom.names.map(nameOf).join(" / ") : "the bout before"}</em> : <em>to be decided</em>;
  return (
    <div className="bk-story">
      <div className="bk-story-h"><Chip k={k} /><div><b className="being-link" onClick={() => openBeing(k)}>{nameOf(k)}</b><small>{S.seed ? `seed ${S.seed} · ` : ""}{M.name}</small></div>
        <button className="bk-x" onClick={onClose} aria-label="Close">×</button></div>
      <ol>
        {S.items.map((it, n) => (
          <li key={n} className={"k-" + it.kind}>
            <span className="bk-r">{it.node.one || M.rounds[it.node.r].one}</span>
            <span>
              {it.kind === "won" && <>won against {opp(it)}{it.node.row && it.node.row.t ? <small> · {dur(it.node.row.t)}</small> : null}</>}
              {it.kind === "lost" && <>lost to {opp(it)} — out</>}
              {it.kind === "bye" && <>a bye — straight through</>}
              {it.kind === "live" && <><b className="bk-hot">on air now</b> against {opp(it)}</>}
              {it.kind === "ringside" && <>at ringside against {opp(it)} — the bell is close</>}
              {it.kind === "next" && <>next: {opp(it)}{it.node.when ? <small> · {whenTxt(it.node.when)}</small> : null}</>}
              {it.kind === "ifwin" && <>if they win: {opp(it)}{it.node.when ? <small> · {whenTxt(it.node.when)}</small> : null}</>}
            </span>
          </li>
        ))}
        {S.champion && <li className="k-champ"><span className="bk-r">🏆</span><span><b>the champion</b></span></li>}
      </ol>
    </div>
  );
}

function Cup({ M, hl, setHl }) {
  const R = M.rounds.length, W = 196;
  const scroller = useRef(null);
  const story = hl ? storyOf(M, hl) : null;
  const path = useMemo(() => new Set(story ? story.items.map((it) => it.node.id) : []), [story]);
  const jump = (r) => { const el = scroller.current && scroller.current.querySelector(`[data-id^="${M.c}:${r}:"]`); if (el && scroller.current) scroller.current.scrollTo({ left: Math.max(0, el.offsetLeft - 8), behavior: "smooth" }); };
  // open on the round that matters now (on a narrow screen the tree is wider than the view)
  useEffect(() => { const t = setTimeout(() => { if (M.roundNow > 0 && M.roundNow < R) jump(M.roundNow); }, 60); return () => clearTimeout(t); }, [M.c, M.day]);   // eslint-disable-line react-hooks/exhaustive-deps
  const onPick = (k) => setHl(hl === k ? null : k);
  return (
    <div className="bk" style={{ "--bkw": W + "px" }}>
      <div className="bk-jump">{M.rounds.map((rd) => <button key={rd.r} className={rd.r === M.roundNow ? "now" : ""} onClick={() => jump(rd.r)}>{rd.name}</button>)}</div>
      <div className="bk-scroll" ref={scroller}>
        <div className="bk-heads">{M.rounds.map((rd) => <span key={rd.r} className={rd.r === M.roundNow ? "now" : ""} style={{ width: `calc(var(--bkw) + ${rd.r === 0 || rd.r === R - 1 ? 16 : 32}px)` }}>{rd.name}</span>)}</div>
        <div className="bk-tree"><Node M={M} r={R - 1} i={0} hl={hl} path={path} onPick={onPick} />
          <div className={"bk-champ" + (M.champion ? " has" : "")}>{M.champion
            ? <><span>🏆 CHAMPION</span><Chip k={M.champion} /><b className="being-link" onClick={() => openBeing(M.champion)}>{nameOf(M.champion)}</b></>
            : <span>🏆 the champion</span>}</div></div>
      </div>
      {M.mixed && <p className="bk-key"><i className="d-c" /> Circuit (C) <i className="d-k" /> Contenders (K) — seeded within their own side.</p>}
      {hl ? <Story M={M} k={hl} onClose={() => setHl(null)} /> : <p className="bk-tip">Tap a fighter to follow their road.</p>}
    </div>
  );
}

/** Friday's Apocalypse: best-of-3 series. */
function Apo({ A, hl, setHl }) {
  const g = (s, x) => {
    const w = x.winner;
    const lab = x.state === "done" ? (w === s.a ? nameOf(s.a) : nameOf(s.b)) : x.state === "live" ? "● LIVE" : x.state === "ringside" ? "at ringside" : x.state === "skipped" ? "not needed" : x.state === "maybe" ? "only if 1–1" : whenTxt(x.when);
    return <span key={x.g} className={"bk-g st-" + x.state}><em>Game {x.g + 1}</em>{lab}</span>;
  };
  return (
    <div className="bk bk-apo">
      {A.safe && <p className="bk-key"><b className="being-link" onClick={() => openBeing(A.safe)}>{nameOf(A.safe)}</b> won the Losers' Cup — safe.</p>}
      {A.series.map((s) => {
        const w = s.done ? s.winner : null;
        return (
          <div key={s.i} className={"bk-series" + (s.done ? " done" : "")}>
            {[s.a, s.b].map((k, n) => (
              <div key={k} className={"bk-s" + (w === k ? " win" : w ? " lose" : "") + (hl === k ? " me" : "")} role="button" tabIndex={0} onClick={() => setHl(hl === k ? null : k)} onKeyDown={(e) => { if (e.key === "Enter") setHl(hl === k ? null : k); }}>
                <Chip k={k} /><b>{nameOf(k)}</b><small>{n ? "Contender" : "Circuit"}</small>
                <span className="bk-pips">{[0, 1].map((p) => <i key={p} className={s.wins[n] > p ? "on" : ""} />)}</span>
                {w === k && <i className="bk-ok">✓</i>}
              </div>
            ))}
            <div className="bk-games">{s.games.map((x) => g(s, x))}</div>
            <small className="bk-f">{s.done ? (s.winner === s.b ? "up to the Circuit" : "the seat is saved") : "the Circuit seat is on the line · best of 3"}</small>
          </div>
        );
      })}
    </div>
  );
}

export default function Brackets({ C, st, onJump = null }) {
  const week = C.week, now = serverNow();
  const today = racingDayOf(now);
  const days = useMemo(() => daysOf(week), [week]);
  const [day, setDay] = useState(() => { const t = days.find((d) => d.day === today) || days.find((d) => d.day > today) || days[days.length - 1]; return t ? t.day : today; });
  const [sel, setSel] = useState(null);
  const [hl, setHl] = useState(null);
  const ctx = useMemo(() => ({ league: C.league, weekRows: C.rows, seasonRows: C.season ? C.season.rows : null, entered: C.entered || null }), [C.league, C.rows, C.season, C.entered]);
  const fights = useMemo(() => [st && st.prev, st && st.cur, st && st.next].filter(Boolean).flatMap((H) => H.bouts || []), [st]);
  const d = days.find((x) => x.day === day);
  const night = useMemo(() => (d && C.league ? nightOf(day, ctx, fights, now) : []), [d, day, ctx, fights, C.league]);   // eslint-disable-line react-hooks/exhaustive-deps
  const apoF = useMemo(() => (d && d.apo && C.league ? apoOf(week, ctx, fights) : null), [d, week, ctx, fights, C.league]);
  const tiles = [...night.map((x) => ({ id: x.c, status: x.status, name: cupShort(x.c), line: x.line, M: x.M })), ...(apoF ? [{ id: "apo", status: apoF.series.every((s) => s.done) ? "done" : apoF.series.some((s) => s.games.some((g) => g.state === "live" || g.state === "ringside")) ? "live" : "soon", name: "Apocalypse", line: `${apoF.series.filter((s) => s.done).length} of ${apoF.series.length} series settled`, M: null }] : [])];
  const dflt = (tiles.find((t) => t.status === "live") || tiles.find((t) => t.status === "soon") || tiles.find((t) => t.status === "later") || tiles[tiles.length - 1] || {}).id;
  const pick = tiles.some((t) => t.id === sel) ? sel : dflt;
  const cur = tiles.find((t) => t.id === pick);
  useEffect(() => { setSel(null); setHl(null); }, [day]);
  if (!C.league) return <p className="on-note">The League is still loading…</p>;
  if (!days.length) return <p className="on-note">No tournaments this week yet.</p>;
  return (
    <div className="bkp">
      <div className="bk-days">{days.map((x) => <button key={x.day} className={(x.day === day ? "on " : "") + (x.day === today ? "today" : "")} onClick={() => setDay(x.day)}>{dayTxt(x.day)}{x.day === today && <i>today</i>}</button>)}</div>
      <div className="bk-night">
        {tiles.map((t) => {
          const M = t.M, dots = M ? M.rounds.flatMap((r) => r.nodes).filter((n) => n.state !== "bye" && n.state !== "empty") : [];
          return (
            <button key={t.id} className={"bk-tile s-" + t.status + (t.id === pick ? " on" : "")} onClick={() => { setSel(t.id); setHl(null); }}>
              <b>{t.name}</b>
              <span className="bk-dots">{M ? dots.map((n) => <i key={n.id} className={n.state} />) : null}</span>
              <small>{t.status === "live" ? "● " : ""}{t.line}</small>
              {M && M.champion && <small className="bk-win"><Chip k={M.champion} />{nameOf(M.champion)}</small>}
            </button>
          );
        })}
      </div>
      {cur && cur.id === "apo" && apoF && <Apo A={apoF} hl={hl} setHl={setHl} />}
      {cur && cur.M && <><h4 className="bk-title">{cur.M.name}{cur.M.champion ? " · decided" : ` · ${cur.M.played} of ${cur.M.total} fought`}</h4><Cup M={cur.M} hl={hl} setHl={setHl} /></>}
      {onJump && <button className="btn ghost bk-back" onClick={() => onJump("stage")}>Back to the stage</button>}
    </div>
  );
}
